/**
 * LeadsTab
 * Full lead management functionality embedded in dashboard tab
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import Button from '../../common/Button';
import Spinner from '../../common/Spinner';
import Modal from '../../common/Modal';
import ConfirmModal from '../../common/ConfirmModal';
import LeadTable from '../../leads/LeadTable';
import LeadCardView from '../../leads/LeadCardView';
import LeadForm from '../../leads/LeadForm';
import LeadFilters from '../../leads/LeadFilters';
import LeadDetailPane from '../../leads/LeadDetailPane';
import { toast } from 'react-toastify';
import { hasDirectorAccess } from '../../../utils/rolePermissions';

export default function LeadsTab() {
  const { user: currentUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailPane, setShowDetailPane] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingLead, setDeletingLead] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [limit] = useState(50);

  // New state for enhanced features
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [agents, setAgents] = useState([]);
  const [leaderViewMode, setLeaderViewMode] = useState('team'); // 'team' or 'personal' for leaders

  // Enhanced filters
  const [filters, setFilters] = useState({
    status: '',
    propertyType: '',
    projectType: '',
    platform: '',
    search: '',
    heatLevel: '',
    agentId: '',
    dateFrom: '',
    dateTo: ''
  });

  // Fetch leads on mount
  useEffect(() => {
    fetchLeads();
    fetchAgents();
  }, []);

  // Re-fetch when filters, sorting, leaderViewMode, or page changes
  useEffect(() => {
    fetchLeads();
  }, [filters, sortBy, sortOrder, leaderViewMode, page]);

  // Reset to page 1 when filters or sorting change
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [filters, sortBy, sortOrder, leaderViewMode]);

  async function fetchLeads(preserveScroll = false) {
    try {
      if (!preserveScroll) {
        setLoading(true);
      }
      const params = {
        ...filters,
        sortBy,
        sortOrder,
        page,
        limit,
        viewMode: currentUser?.role === 'leader' ? leaderViewMode : undefined
      };
      const response = await api.get('/leads', { params });
      setLeads(response.data.leads);
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages);
        setTotalLeads(response.data.pagination.total);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }

  async function fetchAgents() {
    if (hasDirectorAccess(currentUser?.role) || currentUser?.role === 'manager') {
      try {
        const response = await api.get('/users?role=agent');
        setAgents(response.data.users || []);
      } catch (error) {
        console.error('Failed to fetch agents:', error);
      }
    }
  }

  function handleSort(field, order) {
    setSortBy(field);
    setSortOrder(order);
  }

  async function handleHeatLevelChange(assignmentId, level) {
    try {
      await api.patch(`/leads/assignments/${assignmentId}/status`, {
        heatLevel: level
      });
      toast.success('Heat level updated');

      // Update local state instead of full refresh
      setLeads(prevLeads => prevLeads.map(lead => {
        const assignment = lead.assignments?.find(a => a.id === assignmentId);
        if (assignment) {
          assignment.heatLevel = level;
        }
        return lead;
      }));

      // Update selectedLead if it's the one being modified
      if (selectedLead) {
        const assignment = selectedLead.assignments?.find(a => a.id === assignmentId);
        if (assignment) {
          setSelectedLead({ ...selectedLead });
        }
      }
    } catch (error) {
      toast.error('Failed to update heat level');
    }
  }

  function handleCreateLead() {
    setEditingLead(null);
    setShowModal(true);
  }

  function handleViewLead(lead) {
    setSelectedLead(lead);
    setShowDetailPane(true);
  }

  // Update the selected lead when it changes
  async function handleLeadUpdate() {
    // Fetch the updated lead data
    if (selectedLead) {
      try {
        const response = await api.get(`/leads/${selectedLead.id}`);
        const updatedLead = response.data.lead;

        // Update the selectedLead state
        setSelectedLead(updatedLead);

        // Update in the leads list
        setLeads(prevLeads => prevLeads.map(lead =>
          lead.id === updatedLead.id ? updatedLead : lead
        ));
      } catch (error) {
        console.error('Failed to fetch updated lead:', error);
        // Fall back to full refresh
        fetchLeads(true);
      }
    } else {
      // If no selected lead, just refresh the list
      fetchLeads(true);
    }
  }

  function handleEditLead(lead) {
    setEditingLead(lead);
    setShowModal(true);
  }

  function handleDeleteLead(lead) {
    setDeletingLead(lead);
    setShowDeleteModal(true);
  }

  async function handleConfirmDelete() {
    try {
      setDeleteLoading(true);
      await api.delete(`/leads/${deletingLead.id}`);
      toast.success('Lead deleted successfully');
      setShowDeleteModal(false);
      setDeletingLead(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete lead');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleSubmitLead(formData) {
    try {
      setFormLoading(true);

      if (editingLead) {
        // Update existing lead
        await api.patch(`/leads/${editingLead.id}`, formData);
        toast.success('Lead updated successfully');
      } else {
        // Create new lead
        await api.post('/leads', formData);
        toast.success('Lead created successfully');
      }

      setShowModal(false);
      setEditingLead(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save lead');
    } finally {
      setFormLoading(false);
    }
  }

  function handleCloseModal() {
    setShowModal(false);
    setEditingLead(null);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {/* Tab Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display font-bold text-gray-900">Lead Management</h2>
          <p className="text-gray-600 text-sm">
            {hasDirectorAccess(currentUser?.role) || currentUser?.role === 'manager'
              ? 'Manage interior design & renovation leads'
              : 'Manage leads assigned to your team'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Leader View Toggle (for leaders only) */}
          {currentUser?.role === 'leader' && (
            <div className="leader-toggle">
              <button
                onClick={() => setLeaderViewMode('team')}
                className={`leader-toggle-btn ${
                  leaderViewMode === 'team' ? 'leader-toggle-btn-active' : 'leader-toggle-btn-inactive'
                }`}
              >
                Team Leads
              </button>
              <button
                onClick={() => setLeaderViewMode('personal')}
                className={`leader-toggle-btn ${
                  leaderViewMode === 'personal' ? 'leader-toggle-btn-active' : 'leader-toggle-btn-inactive'
                }`}
              >
                Personal Leads
              </button>
            </div>
          )}

          {/* Desktop View Toggle */}
          <div className="hidden md:flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm">
            <button
              onClick={() => setViewMode('table')}
              className={`view-toggle-btn ${viewMode === 'table' ? 'view-toggle-btn-active' : 'view-toggle-btn-inactive'}`}
              title="Table View"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
              </svg>
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`view-toggle-btn ${viewMode === 'cards' ? 'view-toggle-btn-active' : 'view-toggle-btn-inactive'}`}
              title="Card View"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 12a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zM11 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V4zM11 12a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"/>
              </svg>
            </button>
          </div>

          <Button variant="primary" onClick={handleCreateLead}>
            + Add New Lead
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card">
          <p className="text-gray-600 text-sm mb-1">Total Leads</p>
          <p className="text-3xl font-bold text-tenq-gold">{totalLeads}</p>
        </div>
        <div className="card">
          <p className="text-gray-600 text-sm mb-1">New Leads (Page)</p>
          <p className="text-3xl font-bold text-blue-600">
            {leads.filter(l => l.assignments && l.assignments.length > 0 && l.assignments[0]?.status === 'new').length}
          </p>
        </div>
        <div className="card">
          <p className="text-gray-600 text-sm mb-1">Appointments Set (Page)</p>
          <p className="text-3xl font-bold text-green-600">
            {leads.filter(l => l.assignments && l.assignments.length > 0 && l.assignments[0]?.status === 'appt_set').length}
          </p>
        </div>
        <div className="card">
          <p className="text-gray-600 text-sm mb-1">Booked (Page)</p>
          <p className="text-3xl font-bold text-emerald-600">
            {leads.filter(l => l.assignments && l.assignments.length > 0 && l.assignments[0]?.status === 'booked').length}
          </p>
        </div>
      </div>

      {/* Enhanced Filters */}
      <LeadFilters
        filters={filters}
        onFilterChange={setFilters}
        agents={agents}
        showAgentFilter={hasDirectorAccess(currentUser?.role) || currentUser?.role === 'manager'}
      />

      {/* Leads Display */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-display font-bold">
            All Leads ({totalLeads} total, showing {leads.length} on page {page})
          </h2>
          <div className="text-sm text-gray-600">
            Sorted by: <span className="font-medium capitalize">
              {sortBy === 'createdAt' ? 'Date' : sortBy === 'heat' ? 'Heat Level' : sortBy === 'lastActivity' ? 'Last Activity' : sortBy}
            </span>
            <span className="ml-2">({sortOrder === 'ASC' ? 'Oldest First' : 'Newest First'})</span>
          </div>
        </div>

        {/* Desktop Table View */}
        {viewMode === 'table' && (
          <LeadTable
            leads={leads}
            onView={handleViewLead}
            onEdit={handleEditLead}
            onDelete={handleDeleteLead}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onHeatLevelChange={handleHeatLevelChange}
          />
        )}

        {/* Card View (both mobile and desktop when cards mode is active) */}
        {viewMode === 'cards' && (
          <LeadCardView
            leads={leads}
            onLeadClick={handleViewLead}
            onHeatLevelChange={handleHeatLevelChange}
          />
        )}

        {/* Mobile always shows cards on smaller screens */}
        <div className="md:hidden">
          {viewMode === 'table' && (
            <LeadCardView
              leads={leads}
              onLeadClick={handleViewLead}
              onHeatLevelChange={handleHeatLevelChange}
            />
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t pt-4">
            <div className="text-sm text-gray-600">
              Showing page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline-gold"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {page > 2 && (
                  <>
                    <button
                      onClick={() => setPage(1)}
                      className="px-3 py-1 rounded text-sm hover:bg-gray-100"
                    >
                      1
                    </button>
                    {page > 3 && <span className="px-2 text-gray-400">...</span>}
                  </>
                )}

                {page > 1 && (
                  <button
                    onClick={() => setPage(page - 1)}
                    className="px-3 py-1 rounded text-sm hover:bg-gray-100"
                  >
                    {page - 1}
                  </button>
                )}

                <button
                  className="px-3 py-1 rounded text-sm bg-tenq-gold text-white"
                >
                  {page}
                </button>

                {page < totalPages && (
                  <button
                    onClick={() => setPage(page + 1)}
                    className="px-3 py-1 rounded text-sm hover:bg-gray-100"
                  >
                    {page + 1}
                  </button>
                )}

                {page < totalPages - 1 && (
                  <>
                    {page < totalPages - 2 && <span className="px-2 text-gray-400">...</span>}
                    <button
                      onClick={() => setPage(totalPages)}
                      className="px-3 py-1 rounded text-sm hover:bg-gray-100"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <Button
                variant="outline-gold"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Lead Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingLead ? 'Edit Lead' : 'Create New Lead'}
        size="lg"
      >
        <LeadForm
          lead={editingLead}
          onSubmit={handleSubmitLead}
          onCancel={handleCloseModal}
          loading={formLoading}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Lead"
        message={`Are you sure you want to delete the lead "${deletingLead?.name || 'Unknown'}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteLoading}
        danger
      />

      {/* Lead Detail Pane */}
      <LeadDetailPane
        lead={selectedLead}
        isOpen={showDetailPane}
        onClose={() => setShowDetailPane(false)}
        onEdit={handleEditLead}
        onUpdate={handleLeadUpdate}
      />
    </>
  );
}
